import { useRef } from 'react';
import type { ViewFilterNode } from '../../../data/client';
import { cx, Icon, PanEdges, usePanEdges } from '../../../ui';
import { MOTION, useMotion } from '../../../ui/motion';
import { type FilterToken, removeFilterToken } from './filterTokens';
import type { RecordSegment } from './TableToolbar';
import { usePresentRows } from './usePresentRows';

/**
 * THE FILTER TOKENS, ON BOTH VIEWPORTS (wave 5 round 4, B4; spec/screens/list.md section 2
 * item 6 on the Mac and section 3 item 2 on the phone).
 *
 * WHY IT IS ITS OWN FILE NOW. The strip was written inside `ListHeaderRow`, which the page
 * renders only from the desktop breakpoint up - so at 375 the list had no tokens at all and
 * the only way to read what narrowed it was to open the Adjust sheet and drill into Filter.
 * The spec puts the same row under the phone's navigation bar, and a second copy of the
 * markup is how two rows start disagreeing about what a token is. One strip, two callers.
 *
 * THE ENTER AND THE EXIT ARE THE ROW FAMILY (`docs/decisions/motion.md` section 2, "List row
 * insert and remove"): `MOTION.row` on the token with `usePresentRows` around the list, which
 * keeps a removed token in its slot until its own exit settles. The IDENTITY is the token's
 * sentence and not its index - removing the first token renumbers every one after it, and a
 * list keyed by index would fade out the wrong one and re-enter the rest.
 *
 * A TOKEN HAS TWO HALVES AND ONLY ONE OF THEM GIVES (`filterTokens.ts`): the field name and
 * the operator are the head and are never cut, the operand wears the ellipsis. When the
 * tokens cannot fit even at those minimums the strip PANS, and it is the only element on its
 * row that moves.
 */
export interface FilterTokenStripProps {
  readonly tokens: readonly FilterToken[];
  readonly filter: ViewFilterNode | null;
  readonly onFilterChange: (next: ViewFilterNode | null) => void;
  /** "Show Archived" rides in the strip as a token while it is on (spec item 6). */
  readonly segment: RecordSegment;
  readonly onSegmentChange: (next: RecordSegment) => void;
}

export function FilterTokenStrip({
  tokens,
  filter,
  onFilterChange,
  segment,
  onSegmentChange,
}: FilterTokenStripProps) {
  /**
   * Which side of the strip still holds a token, watched by the same hook the table's and the
   * board's own pans use: it listens to the scroll, to the strip resizing (the pane dragged
   * narrower) and to its CONTENT resizing (a token added or removed).
   */
  const strip = useRef<HTMLDivElement>(null);
  const edges = usePanEdges(strip);

  /**
   * ONE LIST FOR BOTH KINDS OF TOKEN. "Show Archived" is not a filter node - it is the
   * segment - but it enters and leaves the row exactly like one, so it travels in the same
   * presence list under a label that cannot collide with a filter sentence.
   */
  const shown: readonly FilterToken[] =
    segment === 'archived'
      ? [
          ...tokens,
          {
            index: -1,
            label: 'Show archived',
            head: 'Show archived',
            value: undefined,
            removable: true,
          },
        ]
      : tokens;
  const presence = usePresentRows(shown.map((token) => token.label));
  /**
   * WHAT A DEPARTING TOKEN IS STILL DRAWN FROM. Its node has already left the filter, so the
   * current list cannot describe it any more - and a token that lost its head mid-fade would
   * blink into a different shape on the way out. The last shape each label had is remembered
   * here; entries for labels nobody is showing any more cost one string each and are dropped
   * with the component.
   */
  const remembered = useRef(new Map<string, FilterToken>());
  for (const token of shown) remembered.current.set(token.label, token);

  return (
    <div className="list-token-pan">
      <div ref={strip} className="list-token-strip">
        {presence.ids.map((label) => {
          const token = remembered.current.get(label);
          if (token === undefined) return null;
          const present = presence.present(label);
          return (
            <FilterTokenChip
              key={label}
              token={token}
              present={present}
              onExited={() => presence.exited(label)}
              onRemove={() =>
                token.index === -1
                  ? onSegmentChange('active')
                  : onFilterChange(removeFilterToken(filter, token.index))
              }
            />
          );
        })}
      </div>
      {/* THE EDGE THAT SAYS A TOKEN IS PASSING BEHIND IT, and it is the product's own
          INLINE-axis edge rather than a second recipe: `docs/ios26-mechanisms.md` row
          "Scroll edge effect, INLINE axis" records `PanEdges` as this product's answer for
          a region that pans sideways, painted only while that side really holds content. */}
      <PanEdges start={edges.start} end={edges.end} />
    </div>
  );
}

function FilterTokenChip({
  token,
  present,
  onExited,
  onRemove,
}: {
  readonly token: FilterToken;
  readonly present: boolean;
  readonly onExited: () => void;
  readonly onRemove: () => void;
}) {
  const motion = useMotion(present, { onExited });
  return (
    <span
      ref={motion.ref}
      data-state={motion.state}
      className={cx('list-token', MOTION.row)}
      title={token.label}
    >
      <span className="list-token-head">{token.head}</span>
      {token.value !== undefined && <span className="list-token-value">{token.value}</span>}
      {/* A DEPARTING TOKEN LOSES ITS REMOVE CONTROL. Its node is already off the filter and
          the index it carried now addresses the child that took its place, so a second click
          during the fade would remove a condition the reader never touched. */}
      {token.removable && present && (
        <button
          type="button"
          className="hit-expand list-token-remove"
          aria-label={`Remove filter: ${token.label}`}
          onClick={onRemove}
        >
          <Icon name="close" size="0.75rem" />
        </button>
      )}
    </span>
  );
}
